import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Toggle,
  Button,
  Alert,
  Spinner,
  ColumnLayout,
  Box,
} from '@cloudscape-design/components';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import axios from 'axios';

export default function SmtpSettings() {
  const { user, isSuperAdmin } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const token = user?.access_token || user?.token;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [host, setHost] = useState('smtp.gmail.com');
  const [port, setPort] = useState('587');
  const [useTls, setUseTls] = useState(true);
  const [fromEmail, setFromEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/smtp-config/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        const d = res.data.data;
        setHost(d.host || 'smtp.gmail.com');
        setPort(String(d.port ?? 587));
        setUseTls(d.use_tls !== false);
        setFromEmail(d.from_email || '');
        setUsername(d.username || '');
        setHasPassword(d.has_password || false);
        setIsActive(d.is_active || false);
        setUpdatedAt(d.updated_at || null);
      }
    } catch (err) {
      notifyError('Failed to load SMTP configuration');
    } finally {
      setLoading(false);
    }
  }, [token, notifyError]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchConfig();
    }
  }, [isSuperAdmin, fetchConfig]);

  const handleSave = async () => {
    if (!host.trim()) {
      notifyError('SMTP host is required');
      return;
    }
    if (!fromEmail.trim()) {
      notifyError('From email is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        host: host.trim(),
        port: parseInt(port, 10) || 587,
        use_tls: useTls,
        from_email: fromEmail.trim(),
        username: username.trim(),
        is_active: isActive,
      };
      if (password) {
        body.password = password;
      }
      const res = await axios.put('/api/admin/smtp-config/update/', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        const d = res.data.data;
        setHost(d.host);
        setPort(String(d.port));
        setUseTls(d.use_tls);
        setFromEmail(d.from_email || '');
        setUsername(d.username || '');
        setHasPassword(d.has_password);
        setIsActive(d.is_active);
        setUpdatedAt(d.updated_at);
        setPassword('');
        notifySuccess('SMTP configuration saved successfully');
      }
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to save SMTP configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) {
      notifyError('Enter a recipient email address for the test');
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        '/api/admin/smtp-config/test/',
        {
          to_email: testEmail.trim(),
          host: host.trim(),
          port: parseInt(port, 10) || 587,
          use_tls: useTls,
          from_email: fromEmail.trim(),
          username: username.trim(),
          ...(password ? { password } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      if (res.data?.status === 'success') {
        notifySuccess('Test email sent successfully');
      }
    } catch (err) {
      notifyError(err.response?.data?.message || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <AppLayoutWrapper
        header={{ title: 'SMTP Settings' }}
        breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/smtp-settings' }, { text: 'SMTP Settings' }]}
      >
        <Alert type="error" header="Access Denied">
          You need super admin privileges to access this page.
        </Alert>
      </AppLayoutWrapper>
    );
  }

  return (
    <AppLayoutWrapper
      header={{
        title: 'SMTP Settings',
        description: 'Configure the global SMTP mailbox used to send verification emails and notifications.',
        actions: (
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        ),
      }}
      breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/smtp-settings' }, { text: 'SMTP Settings' }]}
    >
      <SpaceBetween size="l">
        {loading && (
          <Box textAlign="center">
            <Spinner size="large" />
          </Box>
        )}

        {!loading && (
          <>
            <Alert type="info" header="How this works">
              New user accounts receive an email verification link sent through this mailbox.
              The password is stored in plain text in the database and never returned by the API.
            </Alert>

            <Container>
              <Form>
                <SpaceBetween size="l">
                  <FormField
                    label="Enable SMTP sending"
                    description="When disabled, verification emails will not be sent."
                  >
                    <Toggle
                      checked={isActive}
                      onChange={({ detail }) => setIsActive(detail.checked)}
                    >
                      {isActive ? 'Enabled' : 'Disabled'}
                    </Toggle>
                  </FormField>

                  <ColumnLayout columns={2}>
                    <FormField
                      label="SMTP host"
                      description="e.g. smtp.gmail.com, smtp.office365.com"
                      errorText={!host.trim() ? 'Required' : undefined}
                    >
                      <Input
                        value={host}
                        onChange={({ detail }) => setHost(detail.value)}
                        placeholder="smtp.gmail.com"
                      />
                    </FormField>

                    <FormField
                      label="Port"
                      description="Common ports: 587 (TLS), 465 (SSL), 25"
                    >
                      <Input
                        value={port}
                        onChange={({ detail }) => setPort(detail.value)}
                        placeholder="587"
                      />
                    </FormField>
                  </ColumnLayout>

                  <FormField
                    label="Use TLS"
                    description="Enable STARTTLS for the connection (recommended for port 587)."
                  >
                    <Toggle
                      checked={useTls}
                      onChange={({ detail }) => setUseTls(detail.checked)}
                    >
                      {useTls ? 'TLS enabled' : 'TLS disabled'}
                    </Toggle>
                  </FormField>

                  <ColumnLayout columns={2}>
                    <FormField
                      label="From email"
                      description="Sender address shown to recipients"
                      errorText={!fromEmail.trim() ? 'Required' : undefined}
                    >
                      <Input
                        type="email"
                        value={fromEmail}
                        onChange={({ detail }) => setFromEmail(detail.value)}
                        placeholder="no-reply@adexgenie.ai"
                      />
                    </FormField>

                    <FormField
                      label="Username"
                      description="SMTP login (defaults to from email if empty)"
                    >
                      <Input
                        value={username}
                        onChange={({ detail }) => setUsername(detail.value)}
                        placeholder="SMTP username"
                      />
                    </FormField>
                  </ColumnLayout>

                  <FormField
                    label="Password"
                    description={
                      hasPassword
                        ? 'A password is already set. Enter a new value to replace it.'
                        : 'SMTP password or app password.'
                    }
                  >
                    <Input
                      type="password"
                      value={password}
                      onChange={({ detail }) => setPassword(detail.value)}
                      placeholder={hasPassword ? '••••••••••••••••' : 'SMTP password'}
                    />
                  </FormField>

                  {updatedAt && (
                    <Box color="text-body-secondary" fontSize="body-s">
                      Last updated: {new Date(updatedAt).toLocaleString()}
                    </Box>
                  )}

                  {isActive && fromEmail && host && (
                    <Alert type="success" header="Configuration active">
                      Verification emails will be sent from <strong>{fromEmail}</strong> via{' '}
                      <strong>{host}:{port}</strong>.
                    </Alert>
                  )}
                </SpaceBetween>
              </Form>
            </Container>

            <Container
              header={
                <Header variant="h2" description="Send a test email to verify the configuration works.">
                  Test SMTP
                </Header>
              }
            >
              <SpaceBetween size="m">
                <FormField
                  label="Recipient email"
                  description="The test email will be sent to this address."
                >
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={({ detail }) => setTestEmail(detail.value)}
                    placeholder="you@example.com"
                  />
                </FormField>
                <Button variant="normal" onClick={handleTest} loading={testing}>
                  Send test email
                </Button>
              </SpaceBetween>
            </Container>
          </>
        )}
      </SpaceBetween>
    </AppLayoutWrapper>
  );
}
